import { useEffect, RefObject } from 'react';
import { isTimelineItemActive } from './isTimelineItemActive';

interface UseScrollActiveYearIntoViewProps {
  currentSlide: number;
  timeline: Array<{ year: string; indexTo: number; indexFrom: number }>;
  scrollContainerRef: RefObject<HTMLDivElement | null>;
  itemRefs: RefObject<Array<HTMLLIElement | null>>;
  setScrollOffset: (offset: number) => void;
}

export function useScrollActiveYearIntoView({
  currentSlide,
  timeline,
  scrollContainerRef,
  itemRefs,
  setScrollOffset,
}: UseScrollActiveYearIntoViewProps) {
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer || !itemRefs?.current.length) return;

    // Find active item
    const activeIndex = timeline.findIndex((item) => isTimelineItemActive(item, currentSlide));
    if (activeIndex === -1) return;

    const activeEl = itemRefs.current[activeIndex];
    if (!activeEl) return;

    const elOffset = activeEl.offsetLeft;
    const elWidth = activeEl.offsetWidth;
    const containerWidth = scrollContainer.offsetWidth;

    // Center the active item in view
    const targetLeft = elOffset - (containerWidth / 2 - elWidth / 2);
    const clampedLeft = Math.max(0, targetLeft);

    // Skip if already in position (with tolerance)
    const tolerance = 2; // px margin
    if (Math.abs(scrollContainer.scrollLeft - clampedLeft) < tolerance) {
      return;
    } else {
      scrollContainer.scrollTo({
        left: clampedLeft,
        behavior: 'smooth',
      });

      // Keep scroll offset state in sync for control visibility
      setScrollOffset(clampedLeft);
    }

    //Conscious choice to ignore deps warning to avoid excessive calls
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSlide, timeline]);
}
